import React from "react";
import { Card, CardContent } from "@/components/ui/card";

/**
 * SuiteKpi — tessera KPI standardizzata per Dashboard, Presenze, ecc.
 *
 * Props:
 *  - icon        : componente icona Lucide (obbligatorio)
 *  - label       : etichetta uppercase in cima
 *  - value       : valore principale (numero o stringa)
 *  - sub         : sottotitolo/descrizione (opzionale)
 *  - tone        : "neutral" (default) | "accent" | "secondary" | "warning" | "error" | "primary"
 *  - onClick     : rende la card cliccabile
 *  - testid      : data-testid del valore (la card usa `${testid}-card`)
 *  - size        : "sm" | "md" (default)  — sm compatta per inline
 */
export function SuiteKpi({
  icon: Icon,
  label,
  value,
  sub,
  tone = "neutral",
  onClick,
  testid,
  size = "md",
  className = "",
}) {
  const toneIcon = {
    neutral:   "text-suite-neutral-700 bg-suite-neutral-100",
    accent:    "text-suite-accent-hover bg-suite-accent-light",
    secondary: "text-suite-secondary bg-suite-secondary-light",
    warning:   "text-suite-warning bg-suite-warning-light",
    error:     "text-suite-error bg-suite-error-light",
    primary:   "text-suite-primary bg-suite-primary-light",
  }[tone] || "text-suite-neutral-700 bg-suite-neutral-100";

  const valueSize = size === "sm" ? "text-2xl" : "text-3xl";
  const padding   = size === "sm" ? "p-3" : "p-5";
  const iconBox   = size === "sm" ? "w-8 h-8" : "w-10 h-10";
  const iconSize  = size === "sm" ? 16 : 20;

  return (
    <Card
      className={[
        "border-suite-neutral-200 rounded-md bg-suite-surface transition-all duration-150 ease-out",
        onClick ? "cursor-pointer hover:shadow-md hover:border-suite-primary/40 hover:-translate-y-0.5" : "",
        className,
      ].join(" ")}
      onClick={onClick}
      data-testid={testid ? `${testid}-card` : undefined}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(e); } } : undefined}
    >
      <CardContent className={padding}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-suite-neutral-500 font-semibold">
              {label}
            </div>
            <div
              className={`${valueSize} font-bold mt-1.5 text-suite-neutral-800 tabular-nums leading-tight`}
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              data-testid={testid}
            >
              {value}
            </div>
            {sub ? <div className="text-xs text-suite-neutral-500 mt-1 truncate">{sub}</div> : null}
          </div>
          {Icon ? (
            <div className={`${iconBox} rounded-md flex items-center justify-center flex-shrink-0 ${toneIcon}`}>
              <Icon size={iconSize} strokeWidth={1.75} />
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

export default SuiteKpi;
